// Fuentes de datos para el gráfico de crecimiento de inversión

export interface InvestmentGrowthSource {
  name: string; // Nombre de la fuente
  url: string; // URL de la fuente
  year: string; // Año de publicación
  organization: string; // Organización que publicó el estudio
  description: string; // Breve descripción del estudio
}

export const investmentGrowthSources: InvestmentGrowthSource[] = [
  {
    name: 'Informe de ROI en Servicios Digitales',
    url: 'https://www.mckinsey.com/business-functions/marketing-and-sales/our-insights/the-roi-potential-of-digital-marketing',
    year: '2023',
    organization: 'McKinsey & Company',
    description:
      'Análisis detallado del retorno de inversión en servicios digitales y tecnológicos a lo largo del tiempo.',
  },
  {
    name: 'Estudio de Ciclos de Inversión Digital',
    url: 'https://www.gartner.com/en/marketing/research/digital-investment-trends',
    year: '2023',
    organization: 'Gartner',
    description:
      'Investigación sobre las etapas de maduración de inversiones en transformación digital y sus retornos esperados.',
  },
  {
    name: 'Análisis de Retorno Acumulativo',
    url: 'https://www2.deloitte.com/global/en/pages/technology/articles/digital-investment-management.html',
    year: '2023',
    organization: 'Deloitte',
    description:
      'Estudio sobre el crecimiento acumulativo de inversiones en tecnología y servicios digitales.',
  },
  {
    name: 'Métricas de Éxito en Inversión Digital',
    url: 'https://www.forrester.com/report/research-digital-transformation',
    year: '2023',
    organization: 'Forrester Research',
    description:
      'Investigación sobre indicadores clave de rendimiento y puntos de inflexión en inversiones digitales.',
  },
  {
    name: 'Casos de Éxito: ROI en Servicios Tecnológicos',
    url: '#casos-de-exito',
    year: '2023',
    organization: 'Sol In Prime',
    description:
      'Recopilación de datos de más de 150 implementaciones exitosas y sus retornos de inversión documentados.',
  },
];

// Mapeo de etapas a fuentes específicas
export const stageToSourceMap: Record<string, number[]> = {
  'Inversión inicial': [0, 1],
  Adaptación: [1, 3],
  Implementación: [0, 2, 4],
  Optimización: [2, 3],
  Crecimiento: [0, 2, 4],
  Expansión: [1, 3, 4],
  Consolidación: [2, 3],
  Madurez: [0, 4],
};

// Detalles explicativos para cada etapa del ciclo de inversión
export const stageDetails: Record<string, string> = {
  'Inversión inicial':
    'La fase de inversión inicial representa el punto de partida donde se destina capital para implementar soluciones digitales. En esta etapa, el ROI es 0% ya que aún no se han generado retornos. Esta inversión sienta las bases para el crecimiento futuro y típicamente representa entre el 20-30% del presupuesto total del proyecto.',
  Adaptación:
    'Durante la fase de adaptación (1-2 meses), la empresa comienza a integrar las nuevas soluciones en sus procesos existentes. El ROI alcanza un 20%, equivalente a $2,000 adicionales sobre la inversión inicial. Esta etapa se caracteriza por capacitación de personal y ajustes iniciales en la implementación.',
  Implementación:
    'En la fase de implementación (2-3 meses), las soluciones comienzan a funcionar plenamente. El ROI aumenta al 50%, generando $5,000 adicionales. Las empresas empiezan a ver mejoras en eficiencia operativa y reducción de costos manuales, con ahorros promedio del 15% en procesos optimizados.',
  Optimización:
    'La fase de optimización (3-4 meses) se enfoca en refinar y mejorar las soluciones implementadas. El ROI alcanza el 90%, equivalente a $9,000 adicionales. Los datos muestran que las empresas experimentan un aumento del 25% en productividad y una reducción del 20% en tiempo de ejecución de procesos clave.',
  Crecimiento:
    'Durante la fase de crecimiento (4-5 meses), se comienzan a aprovechar plenamente las ventajas competitivas. El ROI llega al 140%, generando $14,000 adicionales. Las empresas reportan un aumento promedio del 30% en capacidad operativa sin necesidad de recursos adicionales significativos.',
  Expansión:
    'En la fase de expansión (5-6 meses), las soluciones permiten escalar operaciones y explorar nuevas oportunidades. El ROI alcanza el 200%, equivalente a $20,000 adicionales. Los estudios muestran que las empresas pueden aumentar su alcance de mercado hasta un 40% sin incrementos proporcionales en costos operativos.',
  Consolidación:
    'La fase de consolidación (6-7 meses) establece las soluciones como parte integral del negocio. El ROI llega al 270%, generando $27,000 adicionales. Las empresas experimentan una mejora del 45% en retención de clientes y un aumento del 35% en valor de vida del cliente.',
  Madurez:
    'En la fase de madurez (7-8 meses), se alcanza el máximo potencial de las soluciones implementadas. El ROI final es del 350%, equivalente a $35,000 adicionales sobre la inversión inicial. Las empresas reportan un aumento del 60% en eficiencia operativa global y una reducción del 40% en costos asociados a procesos manuales o suboptimizados.',
};
